import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2, FiPrinter, FiPlus, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../hooks/useAuth';
import { saveDeck, unsaveDeck, getSavedDecks, getSessionLogs, getUserCustomDecks, deleteCustomDeck } from '../services/firestoreService';
import { DECKS } from '../data/stimuli';
import { useTranslation } from '../i18n/useTranslation';
import '../styles/sequencing.css';
import { OnboardingModal } from '../components/OnboardingModal';
import '../styles/dashboard.css';

// Hand-picked Pexels cover photos for the activity-type tiles (Categories, Math,
// Sequencing, Matching, Fluency) — these are activity types, not stimulus decks,
// so they have no coverImage of their own; chosen once here rather than searched
// at runtime.
const px = (id) => `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop`;
const ACTIVITY_COVERS = {
  categories:  px(31526223), // child sorting toys into labeled bins
  math:        px(8617708),  // boy at a blackboard with math equations
  sequencing:  px(38353230), // caterpillar on a leaf (life-cycle sequencing)
  matching:    px(17542996), // sock matched with clothespins
  fluency:     px(9521671),  // girl reading a book
};

const STORAGE_KEY_ORDER = 'sc-deck-order';
const ADMIN_EMAIL = 'mackayla.omalley00@gmail.com';

function getSavedOrder() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_ORDER);
    if (!raw) return null;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : null;
  } catch { return null; }
}

function applyOrder(decks, order) {
  if (!order) return decks;
  const byName = Object.fromEntries(decks.map(d => [d.name, d]));
  const ordered = order.map(n => byName[n]).filter(Boolean);
  const missing = decks.filter(d => !order.includes(d.name));
  return [...ordered, ...missing];
}

// ── Section header ────────────────────────────────────────────────────────────

const SectionRow = ({ title, onSeeAll }) => {
  const { t } = useTranslation();
  return (
    <div className="nf-section-hd">
      <h2 className="nf-section-title">{title}</h2>
      {onSeeAll && (
        <button className="nf-see-all" onClick={onSeeAll}>
          {t('dash.seeAll')} <span aria-hidden="true">›</span>
        </button>
      )}
    </div>
  );
};

// ── Deck card (carousel) ──────────────────────────────────────────────────────

const DeckCard = ({ name, coverImage, color, emoji, meta, typeLabel, progress, onClick, menuSlot }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const showImg = coverImage && !imgFailed;
  return (
    <div className="nf-card" onClick={onClick}>
      <div className="nf-card-cover" style={{ background: color || '#F3F4F6' }}>
        {showImg
          ? <img src={coverImage} alt={name} draggable={false} onError={() => setImgFailed(true)} />
          : <span className="nf-card-cover-emoji">{emoji || '📚'}</span>
        }
        {menuSlot}
      </div>
      <div className="nf-card-body">
        {typeLabel && <div className="nf-card-type">{typeLabel}</div>}
        <div className="nf-card-name" title={name}>{name}</div>
        <div className="nf-card-meta">{meta}</div>
        {progress != null && (
          <div className="nf-progress-wrap">
            <div className="nf-progress">
              <div className="nf-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="nf-progress-label">{progress}%</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Activity preview (fanned stack of cards peeking at what's inside) ─────────

const ActivityPreviewCard = ({ name, meta, images, color, onClick, disabled }) => {
  const real = images.filter(Boolean).slice(0, 3);
  const blanks = Math.max(0, 3 - real.length);
  const layers = [...Array(blanks).fill(null), ...real];
  return (
    <button className={`nf-act-stack${disabled ? ' nf-act-stack--disabled' : ''}`} onClick={onClick} type="button">
      <div className="nf-act-stack-fan">
        {layers.map((src, i) => (
          <div
            key={i}
            className={`nf-act-stack-layer nf-act-stack-layer-${i}`}
            style={src ? { backgroundImage: `url(${src})` } : { background: color || '#F3F4F6' }}
          />
        ))}
      </div>
      <div className="nf-act-stack-label">
        <span className="nf-act-stack-name">{name}</span>
        <span className="nf-act-stack-meta">{meta}</span>
      </div>
    </button>
  );
};

// ── Carousel (scroll row + prev/next arrows + page dots) ──────────────────────

const Carousel = ({ children }) => {
  const rowRef = useRef(null);
  const [state, setState] = useState({ overflow: false, pageCount: 1, activePage: 0 });

  const recalc = useCallback(() => {
    const row = rowRef.current;
    if (!row) return;
    const overflow = row.scrollWidth > row.clientWidth + 4;
    const pageCount = Math.max(1, Math.round(row.scrollWidth / Math.max(row.clientWidth, 1)));
    const activePage = Math.max(0, Math.round(row.scrollLeft / Math.max(row.clientWidth, 1)));
    setState({ overflow, pageCount, activePage });
  }, []);

  useEffect(() => {
    recalc();
    window.addEventListener('resize', recalc);
    return () => window.removeEventListener('resize', recalc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recalc, children]);

  const scrollToPage = (page) => {
    const row = rowRef.current;
    if (!row) return;
    row.scrollTo({ left: page * row.clientWidth, behavior: 'smooth' });
  };

  const scrollByPage = (dir) => {
    const row = rowRef.current;
    if (!row) return;
    row.scrollBy({ left: dir * row.clientWidth, behavior: 'smooth' });
  };

  return (
    <div className="nf-carousel-wrap">
      <div className="nf-carousel-row">
        {state.overflow && (
          <button
            className="nf-carousel-arrow"
            onClick={() => scrollByPage(-1)}
            disabled={state.activePage <= 0}
            aria-label="Previous"
            type="button"
          >
            <FiChevronLeft size={18} />
          </button>
        )}
        <div className="nf-carousel" ref={rowRef} onScroll={recalc}>
          {children}
        </div>
        {state.overflow && (
          <button
            className="nf-carousel-arrow"
            onClick={() => scrollByPage(1)}
            disabled={state.activePage >= state.pageCount - 1}
            aria-label="Next"
            type="button"
          >
            <FiChevronRight size={18} />
          </button>
        )}
      </div>
      {state.overflow && state.pageCount > 1 && (
        <div className="nf-carousel-dots">
          {Array.from({ length: state.pageCount }).map((_, i) => (
            <button
              key={i}
              className={`nf-dot${i === state.activePage ? ' nf-dot--active' : ''}`}
              onClick={() => scrollToPage(i)}
              aria-label={`Go to page ${i + 1}`}
              type="button"
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Create-new card ───────────────────────────────────────────────────────────

const CreateCard = ({ onClick }) => (
  <button className="nf-card nf-card--create" onClick={onClick} type="button">
    <div className="nf-create-plus"><FiPlus size={22} /></div>
    <div className="nf-create-label">New Deck</div>
  </button>
);

// ── Main page ─────────────────────────────────────────────────────────────────

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const { t, tc } = useTranslation();
  const isAdmin = userData?.role === 'admin' || currentUser?.email === ADMIN_EMAIL;

  const [savedDecks,    setSavedDecks]    = useState([]);
  const [customDecks,   setCustomDecks]   = useState([]);
  const [sessionLogs,   setSessionLogs]   = useState([]);
  const [deckOrder,     setDeckOrder]     = useState(() => getSavedOrder());
  const [customDeckMenu, setCustomDeckMenu] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting,      setDeleting]      = useState(false);
  const [saveToast,     setSaveToast]     = useState('');
  const [printToast,    setPrintToast]    = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem('sc-onboarding-done'));
  const [showHipaaNotice, setShowHipaaNotice] = useState(() => !localStorage.getItem('sc-hipaa-ok'));

  const toastTimer = useRef(null);
  const printTimer = useRef(null);

  useEffect(() => () => {
    clearTimeout(toastTimer.current);
    clearTimeout(printTimer.current);
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    getSavedDecks(currentUser.uid).then(res => { if (res.success) setSavedDecks(res.data); });
    getSessionLogs(currentUser.uid, 40).then(res => { if (res.success) setSessionLogs(res.data); });
    getUserCustomDecks(currentUser.uid).then(res => {
      if (res.success) {
        const sorted = [...res.data].sort((a, b) => {
          const aTime = a.createdAt?.toDate?.()?.getTime() || 0;
          const bTime = b.createdAt?.toDate?.()?.getTime() || 0;
          return bTime - aTime;
        });
        setCustomDecks(sorted);
      }
    });
  }, [currentUser]);

  useEffect(() => {
    if (!customDeckMenu) return;
    const close = () => setCustomDeckMenu(null);
    const id = setTimeout(() => document.addEventListener('click', close), 0);
    return () => { clearTimeout(id); document.removeEventListener('click', close); };
  }, [customDeckMenu]);

  // ── Derived data ────────────────────────────────────────────────────────────

  const orderedDecks = applyOrder(DECKS, deckOrder);

  // "Continue Working" — most recently played, unique decks with last score
  const continueWorking = useMemo(() => {
    const seen = new Set();
    const items = [];
    for (const log of sessionLogs) {
      const name = log.deckName;
      if (!name || seen.has(name)) continue;
      seen.add(name);
      const builtIn = DECKS.find(d => d.name === name);
      const custom  = customDecks.find(d => d.title === name || d.name === name);
      const deck = builtIn || custom;
      if (!deck) continue;
      const pct = log.total > 0 ? Math.round((log.correct / log.total) * 100) : null;
      items.push({ deck, progress: pct, isCustom: !builtIn, deckId: custom?.id });
      if (items.length >= 5) break;
    }
    return items;
  }, [sessionLogs, customDecks]);

  // Saved (favourite) deck objects
  const savedDeckObjects = useMemo(() =>
    savedDecks.map(name => DECKS.find(d => d.name === name)).filter(Boolean),
    [savedDecks]
  );

  // First name for greeting
  const firstName = useMemo(() => {
    const display = currentUser?.displayName || '';
    return display.trim().split(/\s+/)[0] || 'there';
  }, [currentUser]);

  // ── Actions ─────────────────────────────────────────────────────────────────

  const toggleSave = useCallback(async (e, deckName) => {
    e.stopPropagation();
    if (!currentUser) return;
    const isSaved = savedDecks.includes(deckName);
    setSavedDecks(prev => isSaved ? prev.filter(n => n !== deckName) : [...prev, deckName]);
    if (isSaved) {
      await unsaveDeck(currentUser.uid, deckName);
    } else {
      await saveDeck(currentUser.uid, deckName);
      clearTimeout(toastTimer.current);
      setSaveToast(deckName);
      toastTimer.current = setTimeout(() => setSaveToast(''), 2500);
    }
  }, [currentUser, savedDecks]);

  const handleDeleteCustomDeck = async () => {
    if (!confirmDelete || deleting) return;
    setDeleting(true);
    await deleteCustomDeck(confirmDelete.id);
    setCustomDecks(prev => prev.filter(d => d.id !== confirmDelete.id));
    setConfirmDelete(null);
    setDeleting(false);
  };

  const handlePrintClick = (e) => {
    e?.stopPropagation?.();
    setCustomDeckMenu(null);
    clearTimeout(printTimer.current);
    setPrintToast(true);
    printTimer.current = setTimeout(() => setPrintToast(false), 2400);
  };

  const goToDeck = (deck, isCustom, deckId) => {
    const label = deck.title || deck.name;
    if (isCustom && deckId) {
      navigate(`/session/${encodeURIComponent(label)}?deckId=${deckId}`);
    } else {
      navigate(`/session/${encodeURIComponent(deck.name)}`);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="nf-dashboard">

        {/* Modals / overlays */}
        {showOnboarding && (
          <OnboardingModal onDone={() => { localStorage.setItem('sc-onboarding-done', '1'); setShowOnboarding(false); }} />
        )}

        {showHipaaNotice && (
          <div className="hipaa-notice-banner" role="alert">
            <span className="hipaa-notice-icon">⚠️</span>
            <span className="hipaa-notice-text">
              <strong>HIPAA Reminder:</strong> Do not enter real learner names or any PHI. Use initials or codes only.
            </span>
            <button
              className="hipaa-notice-dismiss"
              onClick={() => { localStorage.setItem('sc-hipaa-ok', '1'); setShowHipaaNotice(false); }}
            >
              Got it
            </button>
          </div>
        )}

        {saveToast && (
          <div className="dash-save-toast">{t('dash.savedToFavorites')} "{tc(saveToast)}" {t('dash.toFavorites')}</div>
        )}
        {printToast && (
          <div className="dash-save-toast dash-save-toast--info">{t('dash.printComingSoon')}</div>
        )}

        {/* Delete confirmation */}
        {confirmDelete && (
          <div
            style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
            onClick={() => setConfirmDelete(null)}
          >
            <div
              style={{ background:'#FFFFFF', borderRadius:16, padding:'28px 24px', maxWidth:340, width:'100%', boxShadow:'0 24px 64px rgba(0,0,0,0.45)', border:'1.5px solid #E5E7EB' }}
              onClick={e => e.stopPropagation()}
            >
              <h3 style={{ fontSize:17, fontWeight:700, marginBottom:8 }}>{t('dash.deleteTitle')} "{tc(confirmDelete.name)}"?</h3>
              <p style={{ fontSize:13, color:'#6B7280', marginBottom:24, lineHeight:1.5 }}>
                {t('dash.deleteBody')}
              </p>
              <div style={{ display:'flex', gap:10 }}>
                <button onClick={() => setConfirmDelete(null)} style={{ flex:1, height:42, background:'#F9FAFB', border:'1.5px solid #E5E7EB', borderRadius:10, fontSize:14, fontWeight:600, color:'#374151', cursor:'pointer', fontFamily:'inherit' }}>{t('dash.cancel')}</button>
                <button onClick={handleDeleteCustomDeck} disabled={deleting} style={{ flex:1, height:42, background:'#DC2626', border:'none', borderRadius:10, fontSize:14, fontWeight:700, color:'#fff', cursor:'pointer', fontFamily:'inherit', opacity: deleting ? 0.6 : 1 }}>
                  {deleting ? t('dash.deleting') : t('dash.deleteTitle')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Welcome ── */}
        <div className="nf-welcome">
          <h1 className="nf-welcome-title">Stimuli Central</h1>
          <p className="nf-welcome-sub">{t('dash.welcomeBack')}, {firstName}! 👋</p>
        </div>

        {/* ── Continue Working ── */}
        {continueWorking.length > 0 && (
          <section className="nf-section">
            <SectionRow title={t('dash.continueWorking')} onSeeAll={() => navigate('/data')} />
            <Carousel>
              {continueWorking.map(({ deck, progress, isCustom, deckId }) => (
                <DeckCard
                  key={deck.id || deck.name}
                  name={tc(deck.title || deck.name)}
                  coverImage={deck.coverImage}
                  color={deck.color}
                  emoji={deck.emoji}
                  meta={`${deck.items?.length || 0} ${t('dash.cards')}`}
                  typeLabel={t('dash.flashcardDeck')}
                  progress={progress}
                  onClick={() => goToDeck(deck, isCustom, deckId)}
                />
              ))}
            </Carousel>
          </section>
        )}

        {/* ── My Decks (full-width carousel) ── */}
        <section className="nf-section">
          <SectionRow title={t('nav.myDecks')} onSeeAll={() => navigate('/library')} />
          <Carousel>
            <CreateCard onClick={() => navigate('/create-set')} />

            {/* Custom decks first (most recent) */}
            {customDecks.slice(0, 4).map(deck => {
              const label = deck.title || deck.name;
              const menuOpen = customDeckMenu === deck.id;
              return (
                <div key={deck.id} className="nf-card-wrap">
                  <DeckCard
                    name={tc(label)}
                    coverImage={deck.coverImage}
                    color={deck.color}
                    emoji={deck.emoji || '📚'}
                    meta={`${deck.cards?.length || deck.cardCount || 0} ${t('dash.cards')}`}
                    typeLabel={t('dash.myDeck')}
                    onClick={() => navigate(`/session/${encodeURIComponent(label)}?deckId=${deck.id}`)}
                    menuSlot={
                      <div className="nf-card-menu-wrap" onClick={e => e.stopPropagation()}>
                        <button
                          className="nf-card-dot-btn"
                          onClick={e => { e.stopPropagation(); setCustomDeckMenu(prev => prev === deck.id ? null : deck.id); }}
                        >⋮</button>
                        {menuOpen && (
                          <div className="nf-card-menu">
                            <button className="nf-card-menu-item" onClick={() => { setCustomDeckMenu(null); navigate('/create-deck', { state: { editDeckId: deck.id } }); }}>
                              <FiEdit2 size={12} /> {t('dash.edit')}
                            </button>
                            <button className="nf-card-menu-item nf-card-menu-item--danger" onClick={() => { setCustomDeckMenu(null); setConfirmDelete({ id: deck.id, name: label }); }}>
                              <FiTrash2 size={12} /> {t('dash.delete')}
                            </button>
                            <button className="nf-card-menu-item" onClick={e => { handlePrintClick(e); }}>
                              <FiPrinter size={12} /> {t('dash.print')}
                            </button>
                          </div>
                        )}
                      </div>
                    }
                  />
                </div>
              );
            })}

            {/* Built-in decks */}
            {orderedDecks.map(deck => (
              <DeckCard
                key={deck.name}
                name={tc(deck.name)}
                coverImage={deck.coverImage}
                color={deck.color}
                emoji={deck.emoji}
                meta={`${deck.items?.length || 0} ${t('dash.cards')}`}
                onClick={() => navigate(`/session/${encodeURIComponent(deck.name)}`)}
              />
            ))}
          </Carousel>
        </section>

        {/* ── Activities (fanned card stacks peeking at the real content inside) ── */}
        <section className="nf-section">
          <SectionRow title={t('dash.activitiesTitle')} />
          <Carousel>
            <ActivityPreviewCard
              name={t('dash.flashcardDecks')}
              meta={`${DECKS.length} ${t('lib.officialDecksCount')}`}
              color="#F3E1E4"
              images={orderedDecks.slice(0, 4).map(d => d.items?.[0]?.image).filter(Boolean)}
              onClick={() => navigate('/library')}
            />
            <ActivityPreviewCard
              name={t('dash.categories')}
              meta={t('sort.startSorting')}
              color="#EAE2F8"
              images={orderedDecks.slice(4, 8).map(d => d.items?.[1]?.image).filter(Boolean)}
              onClick={() => navigate('/categories')}
            />
            <ActivityPreviewCard
              name={t('nav.sequencing')}
              meta={t('seqLib.startActivity')}
              color="#FDEBD3"
              images={[ACTIVITY_COVERS.sequencing]}
              onClick={() => navigate('/sequencing')}
            />
            <ActivityPreviewCard
              name={t('dash.matching')}
              meta={t('matchLib.startActivity')}
              color="#FCE7E9"
              images={[ACTIVITY_COVERS.matching]}
              onClick={() => navigate('/matching')}
            />
            <ActivityPreviewCard
              name={t('dash.math')}
              meta={t('dash.mathCta')}
              color="#D6F5DC"
              images={[ACTIVITY_COVERS.math]}
              onClick={() => navigate('/math')}
            />
            <ActivityPreviewCard
              name={t('dash.fluency')}
              meta={t('dash.comingSoon')}
              color="#DCEAFB"
              images={[ACTIVITY_COVERS.fluency]}
              onClick={() => {}}
              disabled
            />
          </Carousel>
        </section>

        {/* ── Favorites ── */}
        {savedDeckObjects.length > 0 && (
          <section className="nf-section">
            <SectionRow title={t('dash.favorites')} onSeeAll={() => navigate('/saved-decks')} />
            <Carousel>
              {savedDeckObjects.map(deck => (
                <DeckCard
                  key={deck.name}
                  name={tc(deck.name)}
                  coverImage={deck.coverImage}
                  color={deck.color}
                  emoji={deck.emoji}
                  meta={`${deck.items?.length || 0} ${t('dash.cards')}`}
                  onClick={() => navigate(`/session/${encodeURIComponent(deck.name)}`)}
                />
              ))}
            </Carousel>
          </section>
        )}


      </div>
    </Layout>
  );
};
